import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PenLine, Image as ImageIcon, Trash2, Save, Eye, Loader2, Send } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Phase 158d — company email signature.
 *
 * Employer sets up a signature block that is appended to every outbound
 * template email (via the `{signature_html}` placeholder). The signature
 * supports:
 *   • Logo image (uploaded to Emergent Managed Storage).
 *   • Rich-text "intro" area above the name line.
 *   • Auto name line that resolves to the logged-in sender's name at send
 *     time (toggle off for a company-brand-only signature).
 *   • Custom text row below the name (role, tagline, contact details, legal).
 *
 * Preview is server-rendered so what the recruiter sees exactly matches the
 * HTML that will land in Gmail/Outlook — inline styles only, no CSS classes.
 */
export default function CompanyEmailSignature() {
    const { user, refresh: refreshUser } = useAuth();
    const canEdit = user?.role === "employer";
    // The role-title is a USER-level field (recruiters can edit their own
    // even on a page where only employer owns the company signature block).
    const canEditOwnTitle = !!user;
    const [roleTitle, setRoleTitle] = useState(user?.role_title || "");
    const [roleTitleBusy, setRoleTitleBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [sig, setSig] = useState({
        enabled: true,
        logo_url: "",
        logo_width_px: 120,
        intro_html: "",
        include_user_name: true,
        custom_text_html: "",
    });
    // Mirror of the last SAVED signature — compared against `sig` to detect
    // real unsaved changes (string-diffing the preview HTML gives false
    // positives because the server renders role in Title Case while the
    // client built it in lowercase).
    const [savedSig, setSavedSig] = useState(null);
    const [previewHtml, setPreviewHtml] = useState("");
    const fileRef = useRef(null);

    const refreshPreview = async () => {
        try {
            const { data } = await api.post("/company/email-signature/preview");
            setPreviewHtml(data?.html || "");
        } catch {
            /* preview is best-effort */
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/company/email-signature");
            const s = data?.signature || {};
            const next = {
                enabled: s.enabled !== false,
                logo_url: s.logo_url || "",
                logo_width_px: Number(s.logo_width_px || 120),
                intro_html: s.intro_html || "",
                include_user_name: s.include_user_name !== false,
                custom_text_html: s.custom_text_html || "",
            };
            setSig(next);
            setSavedSig(next);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => { setRoleTitle(user?.role_title || ""); }, [user?.role_title]);
    useEffect(() => { if (!loading) refreshPreview(); /* eslint-disable-next-line */ }, [loading]);

    const saveRoleTitle = async () => {
        setRoleTitleBusy(true);
        try {
            await api.patch("/auth/me", { role_title: roleTitle.trim() });
            toast.success("Role title saved");
            try { await refreshUser?.(); } catch { /* AuthContext may not expose it */ }
            await refreshPreview();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setRoleTitleBusy(false); }
    };

    // Phase 158g — one-click real send-to-self so recruiters can eyeball
    // Gmail/Outlook rendering (logo, dark-mode, link styling) before any
    // candidate ever receives the signature in anger.
    const [testSendBusy, setTestSendBusy] = useState(false);
    const sendTestEmail = async () => {
        if (testSendBusy) return;
        if (isDirty()) {
            // Avoid "I tweaked the colour and the test email still shows the
            // old one" — gentle nudge, no hard block.
            toast.warning("Save your changes first so the test email uses the latest signature.");
            return;
        }
        setTestSendBusy(true);
        try {
            const { data } = await api.post("/company/email-signature/send-test");
            if (data?.ok === false) {
                // Zepto / Resend bubble up the real reason now — show it so
                // the user can fix the sender config.
                toast.error(data?.error
                    ? `Delivery failed: ${data.error}`
                    : "Delivery failed — check your email provider settings");
            } else {
                toast.success(`Test email sent to ${data?.to || "you"} · open it in Gmail/Outlook to review`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setTestSendBusy(false); }
    };

    const save = async () => {
        if (!canEdit) return;
        setBusy(true);
        try {
            await api.put("/company/email-signature", sig);
            toast.success("Signature saved");
            setSavedSig({ ...sig });
            await refreshPreview();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const onPickFile = () => fileRef.current?.click();

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo must be under 2 MB");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        setUploading(true);
        try {
            const { data } = await api.post("/company/email-signature/logo", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSig((s) => ({ ...s, logo_url: data.url }));
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setUploading(false); }
    };

    const clearLogo = () => setSig((s) => ({ ...s, logo_url: "" }));

    const previewRefreshDebounced = useRef();
    useEffect(() => {
        // Live preview — debounce so we don't hammer the backend on every keypress.
        if (loading) return;
        if (previewRefreshDebounced.current) clearTimeout(previewRefreshDebounced.current);
        previewRefreshDebounced.current = setTimeout(() => { refreshPreview(); }, 500);
        return () => previewRefreshDebounced.current && clearTimeout(previewRefreshDebounced.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig.enabled, sig.logo_url, sig.logo_width_px, sig.intro_html, sig.include_user_name, sig.custom_text_html, roleTitle]);

    // Preview is rendered against the SAVED signature on the server; for live
    // edits we also show a client-side mirror that matches `build_signature_html`
    // so the recruiter sees changes before clicking Save.
    // Preview the CURRENT input (empty string → fall back to raw role);
    // avoids showing a stale saved role title after the user clears the field.
    const roleTitleEffective = roleTitle.trim();
    const roleLabel = roleTitleEffective || (user?.role || "").replace(/\b\w/g, (c) => c.toUpperCase());

    const livePreview = () => {
        if (!sig.enabled) return "";
        const parts = [];
        parts.push(`<div style="margin-top:28px;padding-top:18px;border-top:1px solid #e5e7eb;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;font-size:13px;line-height:1.55;color:#334155;">`);
        if (sig.logo_url) {
            parts.push(`<div style="margin-bottom:10px;"><img src="${sig.logo_url}" alt="logo" width="${sig.logo_width_px}" style="max-width:${sig.logo_width_px}px;height:auto;display:block;"/></div>`);
        }
        if (sig.intro_html?.trim()) {
            parts.push(`<div style="margin-bottom:6px;">${sig.intro_html}</div>`);
        }
        if (sig.include_user_name && user?.name) {
            // Match server-side Title Case for role so the preview matches the
            // outbound email exactly, and omit the " · " when role is empty.
            const roleSuffix = roleLabel
                ? ` <span style="font-weight:400;color:#64748b;">· ${escapeHtml(roleLabel)}</span>`
                : "";
            parts.push(`<div style="font-weight:700;color:#0f172a;">${escapeHtml(user.name)}${roleSuffix}</div>`);
        }
        if (sig.custom_text_html?.trim()) {
            parts.push(`<div style="margin-top:4px;color:#475569;">${sig.custom_text_html}</div>`);
        }
        parts.push("</div>");
        return parts.join("");
    };

    // Shallow-compare saved vs current — ignore the preview HTML string
    // (debounced server-rendered copy) because it legitimately lags behind.
    const isDirty = () => {
        if (!savedSig) return false;
        return (
            savedSig.enabled !== sig.enabled
            || savedSig.logo_url !== sig.logo_url
            || savedSig.logo_width_px !== sig.logo_width_px
            || savedSig.intro_html !== sig.intro_html
            || savedSig.include_user_name !== sig.include_user_name
            || savedSig.custom_text_html !== sig.custom_text_html
        );
    };

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-6 lg:p-8 space-y-6" data-testid="company-email-signature-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Workspace · Email Signature</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Email signature</h1>
                <p className="text-muted-foreground mt-2 max-w-3xl">
                    A standard signature that gets appended to every outbound template email. The sender's name is filled in
                    automatically from the logged-in user; add a logo, a tagline above the name, and any custom text below
                    (role, phone, legal, scheduling link).
                </p>
                {!canEdit && (
                    <div className="mt-3 inline-block text-[11px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-2.5 py-1">
                        Read-only · only your employer admin can edit
                    </div>
                )}
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground font-mono">Loading…</div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* ─── Editor ─────────────────────────── */}
                    <section className="border border-border bg-white" data-testid="sig-editor">
                        <div className="px-5 py-4 border-b border-border bg-secondary/40 flex items-center gap-2">
                            <PenLine className="w-4 h-4" />
                            <h2 className="font-display font-bold text-base">Edit signature</h2>
                        </div>
                        <div className="p-5 space-y-5">
                            <div className="flex items-center gap-3">
                                <Switch checked={sig.enabled} onCheckedChange={(v) => setSig({ ...sig, enabled: v })}
                                    disabled={!canEdit} data-testid="sig-enabled" />
                                <Label className="text-xs uppercase tracking-widest font-bold">
                                    {sig.enabled ? "Signature on" : "Signature off"}
                                </Label>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Logo</Label>
                                <div className="mt-2 flex items-center gap-3 flex-wrap">
                                    {sig.logo_url ? (
                                        <div className="border border-border p-2 bg-secondary/30">
                                            <img src={sig.logo_url} alt="logo"
                                                 style={{ maxWidth: `${sig.logo_width_px}px`, height: "auto", display: "block" }}
                                                 data-testid="sig-logo-preview" />
                                        </div>
                                    ) : (
                                        <div className="border border-dashed border-border p-6 bg-secondary/20 text-xs font-mono text-muted-foreground">
                                            No logo uploaded
                                        </div>
                                    )}
                                    <input ref={fileRef} type="file" className="hidden"
                                           accept="image/png,image/jpeg,image/webp,image/gif"
                                           onChange={onUpload} data-testid="sig-logo-file-input" />
                                    <Button type="button" variant="outline" className="rounded-none h-9"
                                            onClick={onPickFile} disabled={!canEdit || uploading}
                                            data-testid="sig-logo-upload-btn">
                                        {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-1.5" />}
                                        {sig.logo_url ? "Replace" : "Upload logo"}
                                    </Button>
                                    {sig.logo_url && (
                                        <Button type="button" variant="outline" className="rounded-none h-9 text-rose-700 border-rose-300"
                                                onClick={clearLogo} disabled={!canEdit}
                                                data-testid="sig-logo-clear-btn">
                                            <Trash2 className="w-4 h-4 mr-1.5" /> Clear
                                        </Button>
                                    )}
                                </div>
                                {sig.logo_url && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">
                                            Width (px)
                                        </Label>
                                        <Input type="number" min={40} max={400}
                                               value={sig.logo_width_px}
                                               onChange={(e) => setSig({ ...sig, logo_width_px: Math.max(40, Math.min(400, Number(e.target.value) || 120)) })}
                                               disabled={!canEdit}
                                               className="rounded-none h-8 w-24 font-mono text-xs"
                                               data-testid="sig-logo-width" />
                                    </div>
                                )}
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Intro (above name)</Label>
                                <div className="mt-2" data-testid="sig-intro-editor-wrap">
                                    <RichTextEditor
                                        value={sig.intro_html}
                                        onChange={(v) => setSig({ ...sig, intro_html: v })}
                                        placeholder="e.g. Warm regards,"
                                        minHeight={100}
                                        testId="sig-intro-editor"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Switch checked={sig.include_user_name}
                                        onCheckedChange={(v) => setSig({ ...sig, include_user_name: v })}
                                        disabled={!canEdit} data-testid="sig-name-toggle" />
                                <Label className="text-xs uppercase tracking-widest font-bold">
                                    Include sender name
                                </Label>
                                <span className="text-[11px] font-mono text-muted-foreground">
                                    (will show "{user?.name || "You"}" at send time)
                                </span>
                            </div>

                            {/* Role title — USER-level override, editable by every signed-in user */}
                            <div className="border border-dashed border-ikb/30 bg-ikb/5 p-3 space-y-2">
                                <Label className="text-xs uppercase tracking-widest font-bold text-ikb">
                                    Your role title (appears next to your name)
                                </Label>
                                <p className="text-[11px] font-mono text-muted-foreground">
                                    Override the generic "{(user?.role || "").toString()}" shown next to your name in outbound emails.
                                    Each teammate sets their own.
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Input
                                        value={roleTitle}
                                        onChange={(e) => setRoleTitle(e.target.value)}
                                        placeholder="e.g. Senior Talent Partner"
                                        className="rounded-none h-9 flex-1 min-w-[220px]"
                                        maxLength={120}
                                        data-testid="sig-role-title-input"
                                    />
                                    <Button type="button" variant="outline"
                                            className="rounded-none h-9"
                                            onClick={saveRoleTitle}
                                            disabled={roleTitleBusy || (roleTitle || "") === (user?.role_title || "")}
                                            data-testid="sig-role-title-save">
                                        {roleTitleBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                                        Save
                                    </Button>
                                </div>
                            </div>

                            <div>
                                <Label className="text-xs uppercase tracking-widest font-bold">Custom text (below name)</Label>
                                <div className="mt-2" data-testid="sig-custom-editor-wrap">
                                    <RichTextEditor
                                        value={sig.custom_text_html}
                                        onChange={(v) => setSig({ ...sig, custom_text_html: v })}
                                        placeholder="e.g. Senior Talent Partner · +91 98xxxxxxxx · Book time with me"
                                        minHeight={120}
                                        testId="sig-custom-editor"
                                    />
                                </div>
                            </div>

                            <div className="pt-2 flex items-center gap-2 flex-wrap">
                                <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10"
                                        disabled={!canEdit || busy} onClick={save}
                                        data-testid="sig-save-btn">
                                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                                    Save signature
                                </Button>
                                <Button variant="outline" className="rounded-none h-10"
                                        onClick={sendTestEmail}
                                        disabled={!user?.email || testSendBusy || isDirty()}
                                        title={!user?.email
                                            ? "Your user account has no email address on file"
                                            : isDirty()
                                                ? "Save your changes first — the test email will use the saved signature"
                                                : `Send a real preview email to ${user?.email}`}
                                        data-testid="sig-send-test-btn">
                                    {testSendBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                                    Send me a test email
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* ─── Preview ─────────────────────────── */}
                    <section className="border border-border bg-white lg:sticky lg:top-6 self-start" data-testid="sig-preview">
                        <div className="px-5 py-4 border-b border-border bg-secondary/40 flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            <h2 className="font-display font-bold text-base">Live preview</h2>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground ml-auto">
                                exactly what the candidate sees
                            </span>
                        </div>
                        <div className="p-5 bg-white">
                            <div className="border border-border p-5 min-h-[260px]">
                                <div className="prose prose-sm max-w-none text-sm text-foreground/90">
                                    <p>Hi Asha,</p>
                                    <p>Really pleased to let you know you've been <strong>shortlisted</strong> for <strong>Senior Backend Engineer</strong>…</p>
                                </div>
                                {sig.enabled ? (
                                    <div dangerouslySetInnerHTML={{ __html: livePreview() }} data-testid="sig-preview-rendered" />
                                ) : (
                                    <div className="mt-6 text-xs font-mono text-muted-foreground border-t border-dashed border-border pt-4"
                                         data-testid="sig-preview-disabled">
                                        Signature is disabled — emails will go out without it.
                                    </div>
                                )}
                            </div>
                            {isDirty() && (
                                <div className="mt-4 text-[11px] font-mono text-amber-800"
                                     data-testid="sig-unsaved-banner">
                                    Unsaved changes above. Click Save to apply them to outbound email.
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}

function escapeHtml(s) {
    return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
